import { PlusIcon, Trash01Icon } from '@langchain/untitled-ui-icons';
import { Checkbox, FormControl, FormLabel, IconButton, Input } from '@mui/joy';
import Button from '@mui/joy/Button';

import { Fragment } from 'react';

import { HostEnvVarSchema } from '../../types/schema';
import { SecretInput } from '../SecretInput';
import { parseEnvFile } from './HostEnvVarsInput.utils';

export function HostEnvVarsInput(props: {
  value: HostEnvVarSchema[];
  onChange: (envVars: HostEnvVarSchema[]) => void;
}) {
  return (
    <div className="flex flex-col items-start gap-4">
      {props.value.map((envVar, idx, lst) => {
        return (
          <Fragment key={idx}>
            <div className="flex flex-col">
              <div className="mb-2 flex items-end gap-2">
                <FormControl required>
                  <FormLabel>Name</FormLabel>
                  <Input
                    type="text"
                    onChange={(e) => {
                      const newEnvVars = props.value.slice(0);
                      newEnvVars[idx] = {
                        ...newEnvVars[idx],
                        name: e.target.value,
                      };

                      props.onChange(newEnvVars);
                    }}
                    onPaste={(e) => {
                      const parsed = parseEnvFile(
                        e.clipboardData.getData('text')
                      );
                      if (parsed.length > 0) {
                        const newEnvVars: HostEnvVarSchema[] =
                          props.value.slice(0);

                        newEnvVars.splice(idx, 1, ...parsed);
                        props.onChange(newEnvVars);

                        e.stopPropagation();
                        e.preventDefault();
                      }
                    }}
                    placeholder="Type name..."
                    value={envVar.name}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel required>Value</FormLabel>
                  {envVar.type === 'secret' ? (
                    <SecretInput
                      onChange={(e) => {
                        const newEnvVars = props.value.slice(0);
                        newEnvVars[idx] = {
                          ...newEnvVars[idx],
                          value: e.target.value,
                        };
                        props.onChange(newEnvVars);
                      }}
                      placeholder="Type secret..."
                      value={envVar.value}
                      required
                    />
                  ) : (
                    <Input
                      type="text"
                      onChange={(e) => {
                        const newEnvVars = props.value.slice(0);
                        newEnvVars[idx] = {
                          ...newEnvVars[idx],
                          value: e.target.value,
                        };
                        props.onChange(newEnvVars);
                      }}
                      placeholder="Type value..."
                      value={envVar.value}
                    />
                  )}
                </FormControl>

                <IconButton
                  variant="outlined"
                  color="neutral"
                  aria-label="Delete"
                  onClick={() =>
                    props.onChange(props.value.filter((_, j) => j !== idx))
                  }
                >
                  <Trash01Icon className="size-4" />
                </IconButton>
              </div>
              <Checkbox
                checked={envVar.type === 'secret'}
                onChange={(e) => {
                  const newEnvVars = props.value.slice(0);
                  newEnvVars[idx] = {
                    ...newEnvVars[idx],
                    type: e.target.checked ? 'secret' : 'default',
                  };
                  props.onChange(newEnvVars);
                }}
                label="Secret"
              />
            </div>

            {idx + 1 !== lst.length && (
              <hr className="w-full border-tertiary" />
            )}
          </Fragment>
        );
      })}

      <Button
        variant="outlined"
        color="neutral"
        startDecorator={<PlusIcon className="size-4" />}
        onClick={() => {
          props.onChange([
            ...props.value,
            { name: '', value: '', type: 'default' },
          ]);
        }}
      >
        Add
      </Button>
    </div>
  );
}

export function HostEnvVarsCompactInput(props: {
  value: HostEnvVarSchema[];
  onChange: (envVars: HostEnvVarSchema[]) => void;
}) {
  return (
    <div className="grid grid-cols-[1fr,1fr,auto] justify-end gap-x-3 gap-y-3">
      {props.value.map((envVar, idx) => (
        <Fragment key={idx}>
          <FormControl required>
            {idx === 0 && <FormLabel>Name</FormLabel>}
            <Input
              type="text"
              onChange={(e) => {
                const newEnvVars = props.value.slice(0);
                newEnvVars[idx] = {
                  ...newEnvVars[idx],
                  name: e.target.value,
                };

                props.onChange(newEnvVars);
              }}
              onPaste={(e) => {
                const parsed = parseEnvFile(e.clipboardData.getData('text'));
                if (parsed.length > 0) {
                  const newEnvVars: HostEnvVarSchema[] = props.value.slice(0);

                  newEnvVars.splice(idx, 1, ...parsed);
                  props.onChange(newEnvVars);

                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
              placeholder="Type name..."
              value={envVar.name}
            />
          </FormControl>

          <FormControl>
            {idx === 0 && <FormLabel required>Value</FormLabel>}
            {envVar.type === 'secret' ? (
              <SecretInput
                onChange={(e) => {
                  const newEnvVars = props.value.slice(0);
                  newEnvVars[idx] = {
                    ...newEnvVars[idx],
                    value: e.target.value,
                  };
                  props.onChange(newEnvVars);
                }}
                placeholder="Type secret..."
                value={envVar.value}
                required
              />
            ) : (
              <Input
                type="text"
                onChange={(e) => {
                  const newEnvVars = props.value.slice(0);
                  newEnvVars[idx] = {
                    ...newEnvVars[idx],
                    value: e.target.value,
                  };
                  props.onChange(newEnvVars);
                }}
                placeholder="Type value..."
                value={envVar.value}
              />
            )}
          </FormControl>
          <div className="flex items-end">
            <div className="flex min-h-10 flex-shrink-0 items-center gap-4">
              <Checkbox
                checked={envVar.type === 'secret'}
                onChange={(e) => {
                  const newEnvVars = props.value.slice(0);
                  newEnvVars[idx] = {
                    ...newEnvVars[idx],
                    type: e.target.checked ? 'secret' : 'default',
                  };
                  props.onChange(newEnvVars);
                }}
                label="Secret"
              />
              <IconButton
                variant="outlined"
                color="neutral"
                aria-label="Delete"
                onClick={() =>
                  props.onChange(props.value.filter((_, j) => j !== idx))
                }
              >
                <Trash01Icon className="size-4" />
              </IconButton>
            </div>
          </div>
        </Fragment>
      ))}

      <div className="flex items-start">
        <Button
          variant="outlined"
          color="neutral"
          startDecorator={<PlusIcon className="size-4" />}
          onClick={() => {
            props.onChange([
              ...props.value,
              { name: '', value: '', type: 'default' },
            ]);
          }}
        >
          Add
        </Button>
      </div>
    </div>
  );
}
